import { t as uiText } from '../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { getCloudRunEndpoint } from '../config/cloudRunConfig';
import { db } from '../lib/firebase';

interface SessionHistoryEntry {
  sessionId: string;
  title: string | null;
}

interface SessionListResponse {
  sessions?: Array<{
    sessionId: string;
    title: string | null;
    turnCount?: number;
  }>;
  hasMore?: boolean;
  nextCursor?: string | null;
}

interface SessionHistoryCacheEntry {
  sessions: SessionHistoryEntry[];
  visibleCount: number;
  hasMore: boolean;
  nextCursor: string | null;
  initialized: boolean;
}

interface AiChatSidebarContentProps {
  onNewChatClick?: () => void;
  onSessionClick?: (sessionId: string) => void;
  selectedSessionId?: string | null;
  resolvedSessionTitle?: {
    sessionId: string;
    title: string;
    requestId: number;
  } | null;
  historyRevalidateRequest?: number;
}

const SESSION_PAGE_SIZE = 20;
const sessionHistoryCache = new Map<string, SessionHistoryCacheEntry>();
const sessionHistoryBackgroundTasks = new Map<string, Promise<void>>();

function buildCacheKey(workspaceId: string, userId: string): string {
  return `${workspaceId}:${userId}`;
}

function mergeSessionHistory(
  existing: SessionHistoryEntry[],
  incoming: SessionHistoryEntry[]
): SessionHistoryEntry[] {
  const merged = new Map<string, SessionHistoryEntry>();

  for (const entry of existing) {
    merged.set(entry.sessionId, entry);
  }

  for (const entry of incoming) {
    const previous = merged.get(entry.sessionId);
    merged.set(entry.sessionId, {
      ...previous,
      ...entry,
      title: entry.title ?? previous?.title ?? null,
    });
  }

  return Array.from(merged.values());
}

function upsertAndPromoteSession(
  existing: SessionHistoryEntry[],
  entry: SessionHistoryEntry
): SessionHistoryEntry[] {
  const existingEntry = existing.find((current) => current.sessionId === entry.sessionId);
  const mergedEntry: SessionHistoryEntry = {
    ...existingEntry,
    ...entry,
    title: entry.title ?? existingEntry?.title ?? null,
  };

  return [
    mergedEntry,
    ...existing.filter((current) => current.sessionId !== entry.sessionId),
  ];
}

// ---------------------------------------------------------------------------
// AiChatSessionRow sub-component
// ---------------------------------------------------------------------------

const AiChatPinIcon: React.FC<{ filled?: boolean }> = ({ filled = false }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.5 1.5L10.5 3.5L8 6L7 5.5L4.5 8.5L3 7L6 4.5L5.5 3.5L8.5 1.5Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill={filled ? 'currentColor' : 'none'}
    />
    <path d="M3 7L1.5 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

interface AiChatSessionRowProps {
  entry: SessionHistoryEntry;
  isSelected: boolean;
  isHovered: boolean;
  isPinned: boolean;
  onSelect: () => void;
  onHover: () => void;
  onHoverEnd: () => void;
  onTogglePin: () => void;
}

const AiChatSessionRow: React.FC<AiChatSessionRowProps> = ({
  entry,
  isSelected,
  isHovered,
  isPinned,
  onSelect,
  onHover,
  onHoverEnd,
  onTogglePin,
}) => (
  <div
    style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
    onMouseEnter={onHover}
    onMouseLeave={onHoverEnd}
  >
    <button
      type="button"
      onClick={onSelect}
      style={{
        flex: 1,
        padding: '8px 12px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: isSelected ? 'var(--workspace-primary, #cfae91)' : isHovered ? 'rgba(0,0,0,0.05)' : 'transparent',
        color: isSelected ? '#fff' : '#333',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, color 0.15s ease',
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: '13px',
          fontWeight: 600,
          lineHeight: 1.4,
          wordBreak: 'break-word',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {entry.title || `${entry.sessionId.substring(0, 12)}...`}
      </div>
    </button>
    {/* Pin button — visible on hover, always visible when pinned */}
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
      title={isPinned ? uiText("Remove pin") : uiText("Pin this")}
      style={{
        flexShrink: 0,
        background: 'transparent',
        border: 'none',
        color: isPinned ? 'var(--workspace-primary, #cfae91)' : '#adb5bd',
        cursor: 'pointer',
        padding: '4px 5px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        opacity: isHovered || isPinned ? 1 : 0,
        transition: 'opacity 0.15s ease',
        pointerEvents: isHovered ? 'auto' : 'none',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = isPinned ? '#888' : 'var(--workspace-primary, #cfae91)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = isPinned ? 'var(--workspace-primary, #cfae91)' : '#adb5bd';
      }}
    >
      <AiChatPinIcon filled={isPinned} />
    </button>
  </div>
);

const AiChatSidebarContent: React.FC<AiChatSidebarContentProps> = ({
  onNewChatClick,
  onSessionClick,
  selectedSessionId = null,
  resolvedSessionTitle = null,
  historyRevalidateRequest = 0,
}) => {
  const { workspaceId = '' } = useParams();
  const [sessionHistory, setSessionHistory] = useState<SessionHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(SESSION_PAGE_SIZE);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [pinnedSessionIds, setPinnedSessionIds] = useState<Map<string, Date>>(new Map());
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeCacheKeyRef = useRef<string | null>(null);
  const activeUserIdRef = useRef<string | null>(null);
  const lastHandledResolvedSessionTitleRef = useRef(resolvedSessionTitle?.requestId ?? 0);
  const lastHandledHistoryRevalidateRequestRef = useRef(historyRevalidateRequest);

  const applyCacheEntry = useCallback((cacheEntry: SessionHistoryCacheEntry) => {
    setSessionHistory(cacheEntry.sessions);
    setVisibleCount(cacheEntry.visibleCount);
    setHasMoreHistory(cacheEntry.hasMore);
    setNextCursor(cacheEntry.nextCursor);
  }, []);

  const updateCacheEntry = useCallback((
    cacheKey: string,
    updater: (current: SessionHistoryCacheEntry | undefined) => SessionHistoryCacheEntry
  ) => {
    const nextEntry = updater(sessionHistoryCache.get(cacheKey));
    sessionHistoryCache.set(cacheKey, nextEntry);

    if (activeCacheKeyRef.current === cacheKey) {
      applyCacheEntry(nextEntry);
    }

    return nextEntry;
  }, [applyCacheEntry]);

  const resolveActiveSessionContext = useCallback(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!workspaceId || !user) {
      return null;
    }

    const cacheKey = buildCacheKey(workspaceId, user.uid);
    activeCacheKeyRef.current = cacheKey;
    activeUserIdRef.current = user.uid;

    return {
      cacheKey,
      userId: user.uid,
    };
  }, [workspaceId]);

  const fetchSessionHistoryPage = useCallback(async (
    userId: string,
    limit: number,
    startAfterCreatedAt?: string | null
  ) => {
    if (!workspaceId) {
      return {
        sessions: [] as SessionHistoryEntry[],
        hasMore: false,
        nextCursor: null as string | null,
      };
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      return {
        sessions: [] as SessionHistoryEntry[],
        hasMore: false,
        nextCursor: null as string | null,
      };
    }

    const idToken = await user.getIdToken();
    const baseUrl = `${getCloudRunEndpoint('ragStream')}/session-list`;
    const res = await fetch(baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        workspaceId,
        userId,
        limit,
        startAfterCreatedAt: startAfterCreatedAt ?? null,
      }),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch session history: HTTP ${res.status}`);
    }

    const data = await res.json() as SessionListResponse;

    return {
      sessions: (data.sessions || [])
        .filter((session) => (session.turnCount ?? 0) > 0)
        .map((session) => ({
          sessionId: session.sessionId,
          title: session.title,
        })),
      hasMore: Boolean(data.hasMore),
      nextCursor: data.nextCursor ?? null,
    };
  }, [workspaceId]);

  useEffect(() => {
    let isCancelled = false;

    const initializeSessionHistory = async () => {
      if (!workspaceId) {
        activeCacheKeyRef.current = null;
        activeUserIdRef.current = null;
        setSessionHistory([]);
        setVisibleCount(SESSION_PAGE_SIZE);
        setHasMoreHistory(false);
        setNextCursor(null);
        return;
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        activeCacheKeyRef.current = null;
        activeUserIdRef.current = null;
        setSessionHistory([]);
        setVisibleCount(SESSION_PAGE_SIZE);
        setHasMoreHistory(false);
        setNextCursor(null);
        return;
      }

      const cacheKey = buildCacheKey(workspaceId, user.uid);
      activeCacheKeyRef.current = cacheKey;
      activeUserIdRef.current = user.uid;

      const cachedEntry = sessionHistoryCache.get(cacheKey);
      if (cachedEntry?.initialized) {
        applyCacheEntry(cachedEntry);
        setIsLoadingHistory(false);

        if (cachedEntry.hasMore && !sessionHistoryBackgroundTasks.has(cacheKey)) {
          void (async () => {
            const backgroundTask = (async () => {
              let cursor = cachedEntry.nextCursor;
              let hasMore = cachedEntry.hasMore;

              while (hasMore && cursor) {
                const page = await fetchSessionHistoryPage(user.uid, SESSION_PAGE_SIZE, cursor);

                if (isCancelled) return;

                const updatedEntry = updateCacheEntry(cacheKey, (current) => ({
                  sessions: mergeSessionHistory(current?.sessions || [], page.sessions),
                  visibleCount: current?.visibleCount ?? SESSION_PAGE_SIZE,
                  hasMore: page.hasMore,
                  nextCursor: page.nextCursor,
                  initialized: true,
                }));

                cursor = updatedEntry.nextCursor;
                hasMore = updatedEntry.hasMore;
              }
            })();

            sessionHistoryBackgroundTasks.set(cacheKey, backgroundTask);
            try {
              await backgroundTask;
            } finally {
              sessionHistoryBackgroundTasks.delete(cacheKey);
            }
          })();
        }
        return;
      }

      setIsLoadingHistory(true);
      try {
        const firstPage = await fetchSessionHistoryPage(user.uid, SESSION_PAGE_SIZE);
        if (isCancelled) return;

        const nextCacheEntry: SessionHistoryCacheEntry = {
          sessions: firstPage.sessions,
          visibleCount: Math.min(SESSION_PAGE_SIZE, firstPage.sessions.length),
          hasMore: firstPage.hasMore,
          nextCursor: firstPage.nextCursor,
          initialized: true,
        };

        sessionHistoryCache.set(cacheKey, nextCacheEntry);
        applyCacheEntry(nextCacheEntry);

        if (firstPage.hasMore && firstPage.nextCursor) {
          const backgroundTask = (async () => {
            let cursor = firstPage.nextCursor;
            let hasMore = firstPage.hasMore;

            while (hasMore && cursor) {
              const page = await fetchSessionHistoryPage(user.uid, SESSION_PAGE_SIZE, cursor);

              if (isCancelled) return;

              const updatedEntry = updateCacheEntry(cacheKey, (current) => ({
                sessions: mergeSessionHistory(current?.sessions || [], page.sessions),
                visibleCount: current?.visibleCount ?? Math.min(SESSION_PAGE_SIZE, firstPage.sessions.length),
                hasMore: page.hasMore,
                nextCursor: page.nextCursor,
                initialized: true,
              }));

              cursor = updatedEntry.nextCursor;
              hasMore = updatedEntry.hasMore;
            }
          })();

          sessionHistoryBackgroundTasks.set(cacheKey, backgroundTask);
          try {
            await backgroundTask;
          } finally {
            sessionHistoryBackgroundTasks.delete(cacheKey);
          }
        }
      } catch (error) {
        console.error('❌ Failed to fetch AI chat session history:', error);
      } finally {
        if (!isCancelled) {
          setIsLoadingHistory(false);
        }
      }
    };

    void initializeSessionHistory();

    return () => {
      isCancelled = true;
    };
  }, [applyCacheEntry, fetchSessionHistoryPage, updateCacheEntry, workspaceId]);

  const ensureBackgroundFetch = useCallback(() => {
    const context = resolveActiveSessionContext();
    if (!context || sessionHistoryBackgroundTasks.has(context.cacheKey)) {
      return;
    }

    const cachedEntry = sessionHistoryCache.get(context.cacheKey);
    if (!cachedEntry?.hasMore || !cachedEntry.nextCursor) {
      return;
    }

    const backgroundTask = (async () => {
      let cursor = cachedEntry.nextCursor;
      let hasMore = cachedEntry.hasMore;

      while (hasMore && cursor) {
        const page = await fetchSessionHistoryPage(context.userId, SESSION_PAGE_SIZE, cursor);

        const updatedEntry = updateCacheEntry(context.cacheKey, (current) => ({
          sessions: mergeSessionHistory(current?.sessions || [], page.sessions),
          visibleCount: current?.visibleCount ?? SESSION_PAGE_SIZE,
          hasMore: page.hasMore,
          nextCursor: page.nextCursor,
          initialized: true,
        }));

        cursor = updatedEntry.nextCursor;
        hasMore = updatedEntry.hasMore;
      }
    })();

    sessionHistoryBackgroundTasks.set(context.cacheKey, backgroundTask);
    void backgroundTask.finally(() => {
      sessionHistoryBackgroundTasks.delete(context.cacheKey);
    });
  }, [fetchSessionHistoryPage, resolveActiveSessionContext, updateCacheEntry]);

  const upsertAndPromoteHistoryEntry = useCallback((entry: SessionHistoryEntry) => {
    const context = resolveActiveSessionContext();
    if (!context) {
      return;
    }

    updateCacheEntry(context.cacheKey, (current) => {
      const previousSessions = current?.sessions || [];
      const isNewEntry = !previousSessions.some((s) => s.sessionId === entry.sessionId);
      const nextSessions = upsertAndPromoteSession(previousSessions, entry);
      const previousVisible = current?.visibleCount ?? SESSION_PAGE_SIZE;
      // 新規エントリを先頭に追加した場合、表示件数も +1 して既存の最後尾が
      // ページから押し出されないようにする。
      const nextVisible = Math.min(
        nextSessions.length,
        Math.max(previousVisible + (isNewEntry ? 1 : 0), 1)
      );
      return {
        sessions: nextSessions,
        visibleCount: nextVisible,
        hasMore: current?.hasMore ?? false,
        nextCursor: current?.nextCursor ?? null,
        initialized: true,
      };
    });
  }, [resolveActiveSessionContext, updateCacheEntry]);

  const revalidateFirstPage = useCallback(async () => {
    const context = resolveActiveSessionContext();
    if (!context) {
      return;
    }

    try {
      const firstPage = await fetchSessionHistoryPage(context.userId, SESSION_PAGE_SIZE);
      updateCacheEntry(context.cacheKey, (current) => {
        const currentSessions = current?.sessions || [];
        const firstPageSessions = firstPage.sessions.map((entry) => {
          const existingEntry = currentSessions.find((currentEntry) => currentEntry.sessionId === entry.sessionId);
          return {
            ...existingEntry,
            ...entry,
            title: entry.title ?? existingEntry?.title ?? null,
          };
        });
        const firstPageSessionIds = new Set(firstPageSessions.map((entry) => entry.sessionId));
        const remainingSessions = currentSessions.filter((entry) => (
          !firstPageSessionIds.has(entry.sessionId)
        ));

        return {
          sessions: [...firstPageSessions, ...remainingSessions],
          visibleCount: Math.max(
            current?.visibleCount ?? Math.min(SESSION_PAGE_SIZE, firstPageSessions.length),
            Math.min(SESSION_PAGE_SIZE, firstPageSessions.length)
          ),
          hasMore: firstPage.hasMore,
          nextCursor: firstPage.nextCursor,
          initialized: true,
        };
      });
    } catch (error) {
      console.error('❌ Failed to revalidate AI chat session history:', error);
    }
  }, [fetchSessionHistoryPage, resolveActiveSessionContext, updateCacheEntry]);

  useEffect(() => {
    if (!resolvedSessionTitle) return;
    if (resolvedSessionTitle.requestId === lastHandledResolvedSessionTitleRef.current) {
      return;
    }

    lastHandledResolvedSessionTitleRef.current = resolvedSessionTitle.requestId;
    upsertAndPromoteHistoryEntry({
      sessionId: resolvedSessionTitle.sessionId,
      title: resolvedSessionTitle.title,
    });
  }, [resolvedSessionTitle, upsertAndPromoteHistoryEntry]);

  useEffect(() => {
    if (historyRevalidateRequest === lastHandledHistoryRevalidateRequestRef.current) {
      return;
    }

    lastHandledHistoryRevalidateRequestRef.current = historyRevalidateRequest;
    void revalidateFirstPage().then(() => {
      ensureBackgroundFetch();
    });
  }, [ensureBackgroundFetch, historyRevalidateRequest, revalidateFirstPage]);

  // ---------------------------------------------------------------------------
  // Pin state — stored in Firestore workspaces/{id}/aiChatPins/{sessionId}
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!workspaceId) return;

    const q = collection(db, `workspaces/${workspaceId}/aiChatPins`);
    const unsubscribe = onSnapshot(q, (snap) => {
      const map = new Map<string, Date>();
      snap.docs.forEach((d) => {
        const pinnedAt = d.data().pinnedAt?.toDate?.() ?? new Date();
        map.set(d.id, pinnedAt);
      });
      setPinnedSessionIds(map);
    });

    return () => unsubscribe();
  }, [workspaceId]);

  const handleToggleAiChatPin = useCallback(
    async (sessionId: string) => {
      if (!workspaceId) return;
      try {
        const ref = doc(db, `workspaces/${workspaceId}/aiChatPins`, sessionId);
        if (pinnedSessionIds.has(sessionId)) {
          await deleteDoc(ref);
        } else {
          await setDoc(ref, { pinnedAt: serverTimestamp() });
        }
      } catch (err) {
        console.error('[AiChatSidebarContent] toggle pin error', err);
      }
    },
    [workspaceId, pinnedSessionIds]
  );

  const filteredSessionHistory = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return sessionHistory;
    }

    return sessionHistory.filter((entry) => {
      const title = entry.title?.toLowerCase() || '';
      const sessionId = entry.sessionId.toLowerCase();
      return title.includes(normalizedQuery) || sessionId.includes(normalizedQuery);
    });
  }, [searchQuery, sessionHistory]);

  const displayedSessionHistory = useMemo(() => {
    if (searchQuery.trim()) {
      return filteredSessionHistory;
    }

    return filteredSessionHistory.slice(0, visibleCount);
  }, [filteredSessionHistory, searchQuery, visibleCount]);

  const pinnedHistory = useMemo(() => {
    return filteredSessionHistory
      .filter((e) => pinnedSessionIds.has(e.sessionId))
      .sort((a, b) => (pinnedSessionIds.get(b.sessionId)?.getTime() ?? 0) - (pinnedSessionIds.get(a.sessionId)?.getTime() ?? 0));
  }, [filteredSessionHistory, pinnedSessionIds]);

  const unpinnedDisplayed = useMemo(
    () => displayedSessionHistory.filter((e) => !pinnedSessionIds.has(e.sessionId)),
    [displayedSessionHistory, pinnedSessionIds]
  );

  const handleHistoryScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || searchQuery.trim()) {
      return;
    }

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom > 32) {
      return;
    }

    setVisibleCount((prev) => {
      const nextCount = Math.min(prev + SESSION_PAGE_SIZE, sessionHistory.length);
      if (nextCount === prev) {
        return prev;
      }

      const cacheKey = activeCacheKeyRef.current;
      if (cacheKey) {
        updateCacheEntry(cacheKey, (current) => ({
          sessions: current?.sessions || sessionHistory,
          visibleCount: nextCount,
          hasMore: current?.hasMore ?? hasMoreHistory,
          nextCursor: current?.nextCursor ?? nextCursor,
          initialized: true,
        }));
      }

      return nextCount;
    });

    if (hasMoreHistory) {
      ensureBackgroundFetch();
    }
  }, [ensureBackgroundFetch, hasMoreHistory, nextCursor, searchQuery, sessionHistory, updateCacheEntry]);

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          padding: '16px 16px 12px',
        }}
      >
        <button
          type="button"
          onClick={() => {
            onNewChatClick?.();
            ensureBackgroundFetch();
          }}
          style={{
            width: '100%',
            padding: '10px 16px',
            border: '1.5px solid var(--workspace-primary, #cfae91)',
            borderRadius: '8px',
            backgroundColor: 'transparent',
            color: 'var(--workspace-primary, #cfae91)',
            fontSize: '14px',
            fontWeight: 600,
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--workspace-primary, #cfae91)';
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {uiText("New chat")}
        </button>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={uiText("Search history")}
          style={{
            width: '100%',
            marginTop: '10px',
            padding: '10px 14px',
            border: '1px solid #ddd',
            borderRadius: '10px',
            backgroundColor: '#fff',
            color: '#333',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--workspace-primary, #cfae91)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = '#ddd';
          }}
        />
      </div>
      <div
        ref={scrollContainerRef}
        onScroll={handleHistoryScroll}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '0 16px 16px',
        }}
      >
        {/* ピン止め section */}
        {!isLoadingHistory && pinnedHistory.length > 0 && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '8px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                fontSize: '11px',
                fontWeight: 700,
                color: '#888',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.5 1.5L10.5 3.5L8 6L7 5.5L4.5 8.5L3 7L6 4.5L5.5 3.5L8.5 1.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                <path d="M3 7L1.5 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              {uiText("Pin this")}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '4px' }}>
              {pinnedHistory.map((entry) => {
                const isSelected = selectedSessionId === entry.sessionId;
                const isHovered = hoveredSessionId === entry.sessionId;
                return (
                  <AiChatSessionRow
                    key={entry.sessionId}
                    entry={entry}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    isPinned={true}
                    onSelect={() => onSessionClick?.(entry.sessionId)}
                    onHover={() => setHoveredSessionId(entry.sessionId)}
                    onHoverEnd={() => setHoveredSessionId(null)}
                    onTogglePin={() => handleToggleAiChatPin(entry.sessionId)}
                  />
                );
              })}
            </div>
          </>
        )}

        {/* 履歴 section */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '8px',
            paddingTop: '12px',
            borderTop: '1px solid rgba(0,0,0,0.08)',
            fontSize: '11px',
            fontWeight: 700,
            color: '#888',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M6 3.5V6L7.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {uiText("History")}
        </div>
        {isLoadingHistory ? (
          <div style={{ fontSize: '13px', color: '#888' }}>
            {uiText("Loading now...")}
          </div>
        ) : unpinnedDisplayed.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#888' }}>
            {sessionHistory.length === 0 ? uiText("No history yet") : pinnedHistory.length > 0 ? uiText("All items are already pinned") : uiText("No matching history")}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {unpinnedDisplayed.map((entry) => {
              const isSelected = selectedSessionId === entry.sessionId;
              const isHovered = hoveredSessionId === entry.sessionId;
              return (
                <AiChatSessionRow
                  key={entry.sessionId}
                  entry={entry}
                  isSelected={isSelected}
                  isHovered={isHovered}
                  isPinned={false}
                  onSelect={() => onSessionClick?.(entry.sessionId)}
                  onHover={() => setHoveredSessionId(entry.sessionId)}
                  onHoverEnd={() => setHoveredSessionId(null)}
                  onTogglePin={() => handleToggleAiChatPin(entry.sessionId)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiChatSidebarContent;
